import type { GeoPt } from './types'
import { ringsToPolygons, unionedRings } from './polygonUnion'

/**
 * Display name shown for a property group whose first parcel carries no name.
 * Property parcels normally arrive with a cadastral designation, so this is a
 * defensive fallback rather than something users usually see.
 */
export const PROPERTY_GROUP_FALLBACK_NAME = 'Property'

/** The minimal shape the editor's Border/Area records share. */
type Groupable = {
  id: string
  name: string
  color?: string
  rings: GeoPt[][]
  outerCount?: number
  areaSource?: string
  areaGroup?: string
}

/**
 * One row/shape the editor draws for the user. A manual (hand-drawn) area maps
 * to exactly one item carrying itself; the parcels of one property selection —
 * each stored as its own area but sharing an `areaGroup` — collapse into a
 * single item so the editor can present them as ONE area, the way mobile does,
 * without changing how they are stored.
 */
export type AreaDisplayItem<T> = {
  /** `areaGroup` for a property group, else the item's own id. Stable list key. */
  key: string
  areaGroup?: string
  /** True only for a collapsed property group (`areaSource==='property'` + `areaGroup`). */
  isGroup: boolean
  name: string
  color?: string
  /** 1 member for an ungrouped item; N (first-seen order) for a property group. */
  members: T[]
  /**
   * The shape to DRAW: outer rings first, then holes, merged across every member's
   * parts by the same resolver the read-only surfaces use (`unionedRings`).
   *
   * This replaced a single-ring `outline` that went `null` whenever the shape needed
   * more than one ring — a disjoint union, a hole, or a member of several parcels —
   * leaving each render site to fall back to the raw per-member rings. That fallback
   * is what made the editor disagree with every other surface: internal seams at full
   * weight, one halo per parcel stacked along them, in the one place the user is
   * actually drawing. A multi-ring shape has no such case to fall back from.
   */
  rings: GeoPt[][]
  /** How many leading entries of {@link rings} are outers rather than holes. */
  outerCount: number
}

/**
 * A row collapses when the areas share a non-empty group id.
 *
 * The group id alone is the app's own grouping key for stored areas — it groups
 * by the tag and never consults where an area came from — and web writes the tag
 * and the source together for every parcel it creates, so dropping the source
 * check changes nothing for existing web data while it stops rejecting a group
 * written by any other client.
 */
function isGroupMember(item: Groupable): boolean {
  return !!item.areaGroup
}

/**
 * Collapse each property selection (its parcels share one `areaGroup`) into a
 * single display item while leaving every other area as its own item, and
 * preserve overall first-appearance order — a group is anchored at the first
 * appearance of any of its members.
 *
 * Storage is untouched: this is a display projection over the per-parcel state.
 */
export function groupAreasForDisplay<T extends Groupable>(items: T[]): AreaDisplayItem<T>[] {
  const result: AreaDisplayItem<T>[] = []
  const groupIndexByKey = new Map<string, number>()

  for (const item of items) {
    if (isGroupMember(item)) {
      const key = item.areaGroup as string
      const existing = groupIndexByKey.get(key)
      if (existing === undefined) {
        groupIndexByKey.set(key, result.length)
        result.push({
          key,
          areaGroup: key,
          isGroup: true,
          name: item.name,
          color: undefined, // resolved once every member is gathered
          members: [item],
          // Finalised once all members are gathered.
          rings: [],
          outerCount: 0,
        })
      } else {
        result[existing].members.push(item)
      }
      continue
    }
    result.push({
      key: item.id,
      areaGroup: undefined,
      isGroup: false,
      name: item.name,
      color: item.color,
      members: [item],
      // An ungrouped item goes through the same resolver, because a SINGLE imported
      // record is routinely several parcels: two touching ones have a seam to dissolve
      // whether they arrived as one record or two, and the read-only surfaces dissolve
      // it either way.
      ...shapeOf([item]),
    })
  }

  // Resolve each group's shape now that all its members are collected.
  for (const index of groupIndexByKey.values()) {
    const group = result[index]
    // The app takes the first non-empty name and the first non-empty colour
    // ACROSS the members, not the first member's: the parcel a selection starts
    // from is whichever one the user tapped first, and it is routinely the one
    // with no designation and no colour of its own.
    group.name = group.members.find((m) => m.name)?.name ?? PROPERTY_GROUP_FALLBACK_NAME;
    group.color = group.members.find((m) => m.color)?.color;
    const shape = shapeOf(group.members)
    group.rings = shape.rings
    group.outerCount = shape.outerCount
  }

  return result
}

/**
 * The drawable shape for one item's members, through the shared resolver.
 *
 * This carried a derived single-ring `outline` alongside the shape, on the reasoning
 * that "one closed ring or nothing" is a real question for a swatch or a centre-on
 * target. It is — but nothing asks it: the centre-on target moved to the shape's outers
 * in the same change that added this, and no swatch ever read it. So the field was
 * production-dead with a doc comment naming a caller that did not exist, and nine test
 * assertions exercising an API the renderer never touches. Both are gone; the contract
 * is the pair the render path actually consumes.
 */
function shapeOf(
  members: readonly Groupable[],
): { rings: GeoPt[][]; outerCount: number } {
  return unionedRings(
    members.flatMap((m) => ringsToPolygons(m.rings, m.outerCount ?? m.rings.length)),
  )
}
